import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { createWarning } from '@core/helpers/create-validator.helper';
import { toNum } from '@shared/helpers/math.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';

@Injectable({ providedIn: 'root' })
export class BaseSocialeWarningsService {
    constructor(private store: Store<Revisione53State>) {}

    my_SociSpecData = createWarning(this.store, (myVerbale) => {
        const my_SociSpecData = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociSpecData);
        const my_SociFisCoopData = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociFisCoopData);
        const my_SociGiuCoopData = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopData);

        return this._isAboveOneThirdOfCoopMembers(my_SociSpecData, my_SociFisCoopData, my_SociGiuCoopData);
    });

    my_SociSpecPrimo = createWarning(this.store, (myVerbale) => {
        const my_SociSpecPrimo = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociSpecPrimo);
        const my_SociFisCoopPrimo = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociFisCoopPrimo);
        const my_SociGiuCoopPrimo = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopPrimo);

        return this._isAboveOneThirdOfCoopMembers(my_SociSpecPrimo, my_SociFisCoopPrimo, my_SociGiuCoopPrimo);
    });

    my_SociSpecSecondo = createWarning(this.store, (myVerbale) => {
        const my_SociSpecSecondo = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociSpecSecondo);
        const my_SociFisCoopSecondo = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociFisCoopSecondo);
        const my_SociGiuCoopSecondo = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopSecondo);

        return this._isAboveOneThirdOfCoopMembers(my_SociSpecSecondo, my_SociFisCoopSecondo, my_SociGiuCoopSecondo);
    });

    numSoci = createWarning(this.store, (myVerbale) => {
        const my_SociFisCoopData = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociFisCoopData);
        const my_SociGiuCoopData = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopData);
        const my_SociSpecData_personeFisiche = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_gr0001.my_SociSpecData_personeFisiche);
        const sommaSoci = my_SociFisCoopData + my_SociGiuCoopData + my_SociSpecData_personeFisiche;
        const showWarning = !(
            isNilOrEmpty(my_SociFisCoopData) ||
            isNilOrEmpty(my_SociGiuCoopData) ||
            isNilOrEmpty(my_SociSpecData_personeFisiche) ||
            my_SociGiuCoopData <= 0 ||
            sommaSoci >= 9
        );

        return showWarning ? 'Attenzione: verificare il numero minimo di soci cooperatori' : null;
    });

    comprovanteULA = createWarning(this.store, (myVerbale) => {
        const contoEconomico = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_ContoEconomico;
        if (isNilOrEmpty(contoEconomico.my_CoEcB9tot1) || isNilOrEmpty(contoEconomico.my_CoEcB9tot2)) {
            return null;
        }
        const my_CoEcB9tot1 = toNum(contoEconomico.my_CoEcB9tot1);
        const my_CoEcB9tot2 = toNum(contoEconomico.my_CoEcB9tot2);
        if (my_CoEcB9tot1 > 0 && my_CoEcB9tot2 > 0) {
            return "Il revisore acquisisca un documento comprovante le ULA se la cooperativa non ha nominato l'organo di controllo.";
        }
    });

    terminiAttuali = createWarning(this.store, (myVerbale) => {
        const my_CkSociSpecDiritti = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_CkSociSpecDiritti;
        if (!isNilOrEmpty(my_CkSociSpecDiritti) && toNum(my_CkSociSpecDiritti) !== 1) {
            return 'Il revisore verifichi i termini degli attuali soci iscritti alla categoria speciale';
        }
    });

    verificareSociPersoneGiuridicheWarning = createWarning(this.store, (myVerbale) => {
        const my_TipoSezEnte = toNum(myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_TipoSezEnte);
        const my_SociGiuCoopData = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopData);
        const my_CkNormeSocioLavorat = toNum(myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_CkNormeSocioLavorat);
        if (my_TipoSezEnte === 5 && my_SociGiuCoopData > 0 && my_CkNormeSocioLavorat === 1) {
            return 'Attenzione: Verificare la presenza di soci cooperatori persone giuridiche';
        }
    });

    private _isAboveOneThirdOfCoopMembers(targetField: number, secondField: number, thirdField: number): string {
        if (targetField > (secondField + thirdField + targetField) / 3 && !isNilOrEmpty(targetField)) {
            return 'Numero superiore ad 1/3 del numero soci cooperatori.';
        } else {
            return null;
        }
    }
}
